import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, WrenchIcon, ZapIcon, ThermometerIcon, HomeIcon, PaintbrushIcon, SparklesIcon, TreesIcon, HammerIcon, ClockIcon, UserIcon, MapPinIcon, ShowerHeadIcon, BathIcon, ArrowUpIcon, SquareIcon, SunIcon, CookingPotIcon, LayersIcon, DoorOpenIcon, WarehouseIcon, ShieldIcon, FenceIcon, SnowflakeIcon, FootprintsIcon, BugIcon, SearchIcon, RefreshCwIcon, HelpCircleIcon, LoaderIcon, CloudRainIcon, TreePineIcon } from 'lucide-react';
import { pxFormConfigs } from '../data/pxFormConfigs';
import { PXQuoteForm } from './PXQuoteForm';
interface QuoteFormProps {
  initialService: string;
  initialZip: string;
  onClose: () => void;
}
export function QuoteForm({
  initialService,
  initialZip,
  onClose
}: QuoteFormProps) {
  // Route window-replacement to specialized flow
  if (initialService === 'window-replacement') {
    return <WindowReplacementForm initialZip={initialZip} onClose={onClose} data-id="element-2334" />;
  }
  if (initialService === 'roofing') {
    return <RoofingQuoteForm initialZip={initialZip} onClose={onClose} data-id="element-2335" />;
  }
  // Route to data-driven PX form if config exists
  if (pxFormConfigs[initialService]) {
    return <PXQuoteForm config={pxFormConfigs[initialService]} initialZip={initialZip} onClose={onClose} data-id="element-2336" />;
  }
  return <GenericQuoteForm initialService={initialService} initialZip={initialZip} onClose={onClose} data-id="element-2337" />;
}
// ─── WINDOW REPLACEMENT FORM (mirrors Champion flow) ───────────────────────
function WindowReplacementForm({
  initialZip,
  onClose
}: {
  initialZip: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const [formData, setFormData] = useState({
    formName: 'windows_replacement_form_v1',
    alid: '',
    oid: '',
    source: 'quote_form',
    subID1: '',
    subID2: '',
    subID3: '',
    EBRDate: '',
    ip: '',
    cert: '',
    projectNature: '',
    windowCount: '',
    homeOwnership: '',
    taskName: 'Windows',
    address1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_primary: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  // Phone formatting
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      phone_primary: formatPhoneNumber(e.target.value)
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch IP
  useEffect(() => {
    try {
      fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
        if (data?.ip) setFormData(prev => ({
          ...prev,
          ip: data.ip
        }));
      }).catch(() => {});
    } catch (e) {}
  }, []);
  const totalSteps = 5;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') setAddressError('');
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.cert;
    const {
      cert,
      ...restFormData
    } = formData;
    const payload = {
      ...restFormData,
      TrustedFormCert: finalCert,
      api_token: '88E0DE0F-E8D4-4C49-8131-7FAB6AD9DC4E'
    };
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const webhookUrl = `https://hooks.zapier.com/hooks/catch/6973915/uxzsuc2/?${params.toString()}`;
    console.log('Submitting to Window Replacement webhook:', webhookUrl);
    try {
      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Webhook call failed, but continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = () => <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-2338">
      <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2339">
        FindService<span className="text-amber-500" data-id="element-2340">Quotes</span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-2341">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2342" />
        <span className="hidden sm:inline" data-id="element-2343">Exit</span>
      </button>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2344">
        <Header data-id="element-2345" />
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-2346">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100" data-id="element-2347">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2348">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-2349" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2350">
              Quote Request Received!
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2351">
              Thank you, {formData.first_name}! A window replacement specialist
              will contact you shortly at {formData.phone_primary} to discuss
              your {formData.projectNature.toLowerCase()} project in{' '}
              {formData.city}.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors" data-id="element-2352">
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2353">
      <Header data-id="element-2354" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-2355" />

      <div className="w-full bg-slate-200 h-2" data-id="element-2356">
        <motion.div className="bg-amber-500 h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-2357" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-2358">
        <div className="w-full max-w-2xl" data-id="element-2359">
          <div className="mb-6 h-8" data-id="element-2360">
            {step > 1 && <button onClick={handleBack} className="text-slate-500 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors" data-id="element-2361">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2362" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-100" data-id="element-2363">
            <AnimatePresence mode="wait" custom={direction} data-id="element-2364">
              {/* STEP 1: Nature of Project */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2365">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2366">
                    What is the nature of your windows project?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2367">
                    {[{
                  value: 'Replace',
                  desc: 'I need to replace existing windows',
                  icon: <RefreshCwIcon className="w-6 h-6" data-id="element-2368" />
                }, {
                  value: 'Repair',
                  desc: 'I need to repair existing windows',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-2369" />
                }, {
                  value: 'Not Sure',
                  desc: "I'm not sure yet — I'd like an expert opinion",
                  icon: <HelpCircleIcon className="w-6 h-6" data-id="element-2370" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('projectNature', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.projectNature === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2371">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.projectNature === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2372">
                          {option.icon}
                        </div>
                        <div data-id="element-2373">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2374">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2375">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: How Many Windows */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2376">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2377">
                    How many windows are involved?
                  </h1>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2378">
                    {['6+', '3-5', '2', '1'].map((count, idx) => <button key={idx} onClick={() => handleOptionSelect('windowCount', count)} className={`p-8 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData.windowCount === count ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2379">
                        <span className="font-bold text-slate-900 text-2xl" data-id="element-2380">
                          {count}
                        </span>
                        <span className="text-slate-500 text-sm" data-id="element-2381">
                          {count === '1' ? 'window' : 'windows'}
                        </span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Homeownership */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2382">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2383">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2384">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No - But I am Allowed to make changes',
                  desc: 'I rent but have permission from my landlord'
                }, {
                  value: 'No',
                  desc: 'I rent and do not have permission'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeOwnership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeOwnership === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2385">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeOwnership === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2386">
                          <UserIcon className="w-6 h-6" data-id="element-2387" />
                        </div>
                        <div data-id="element-2388">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2389">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2390">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: Address */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2391">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2392">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-2393">
                    <div data-id="element-2394">
                      <label htmlFor="address1" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2395">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} placeholder="123 Main St" data-id="element-2396" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-2397">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-2398">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-2399">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2400" />
                            <span data-id="element-2401">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-2402">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2403" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-2404" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full hover:bg-amber-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-2405">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2406" />
                            <span className="text-sm font-semibold text-amber-700" data-id="element-2407">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-amber-400 ml-auto" data-id="element-2408">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full hover:bg-slate-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-2409">
                            <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2410" />
                            <span className="text-sm text-slate-500" data-id="element-2411">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2412">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 5: Contact Info */}
              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2413">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2414">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-slate-500 text-center mb-8" data-id="element-2415">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2416">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-2417">
                      <div data-id="element-2418">
                        <label htmlFor="first_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2419">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="First Name" data-id="element-2420" />
                      </div>
                      <div data-id="element-2421">
                        <label htmlFor="last_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2422">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Last Name" data-id="element-2423" />
                      </div>
                    </div>

                    <div data-id="element-2424">
                      <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2425">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="email@example.com" data-id="element-2426" />
                    </div>

                    <div data-id="element-2427">
                      <label htmlFor="phone_primary" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2428">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_primary" name="phone_primary" required value={formData.phone_primary} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-2429" />
                    </div>

                    <div className="pt-4" data-id="element-2430">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-2431">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0" required data-id="element-2432" />
                        <span className="text-[11px] text-slate-500 leading-tight" data-id="element-2433">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with FindServiceQuotes, including
                          their contractors and affiliates, and up to four home
                          service companies. I authorize them to email, call, or
                          text me for marketing purposes using autodialer
                          technology, AI generated or AI assisted voice, or
                          prerecorded voice messages on the phone number I
                          provided, even if listed on a DoNotCall list. Consent
                          is not a condition to purchase. I agree to the{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2434">
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2435">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30" data-id="element-2436">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}
// ─── ROOFING QUOTE FORM (PX API Flow) ──────────────────────────────────────
function RoofingQuoteForm({
  initialZip,
  onClose
}: {
  initialZip: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const [formData, setFormData] = useState({
    formName: 'roofing_quote_form_v1',
    alid: '',
    oid: '',
    source: 'quote_form',
    subID1: '',
    subID2: '',
    subID3: '',
    EBRDate: '',
    ip: '',
    cert: '',
    project_type: '',
    roofing_material: '',
    stories: '',
    timeline: '',
    insurance_claim: '',
    property_ownership: '',
    taskName: 'Roofing',
    address1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_primary: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  // Phone formatting
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      phone_primary: formatPhoneNumber(e.target.value)
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch IP
  useEffect(() => {
    try {
      fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
        if (data?.ip) setFormData(prev => ({
          ...prev,
          ip: data.ip
        }));
      }).catch(() => {});
    } catch (e) {}
  }, []);
  const totalSteps = 8;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') setAddressError('');
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.cert;
    const {
      cert,
      ...restFormData
    } = formData;
    const payload = {
      ...restFormData,
      TrustedFormCert: finalCert,
      api_token: '88E0DE0F-E8D4-4C49-8131-7FAB6AD9DC4E'
    };
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const webhookUrl = `https://hooks.zapier.com/hooks/catch/6973915/uxzsuc2/?${params.toString()}`;
    console.log('Submitting to Roofing webhook:', webhookUrl);
    try {
      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Webhook call failed, but continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = () => <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-2437">
      <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2438">
        FindService<span className="text-amber-500" data-id="element-2439">Quotes</span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-2440">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2441" />
        <span className="hidden sm:inline" data-id="element-2442">Exit</span>
      </button>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2443">
        <Header data-id="element-2444" />
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-2445">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100" data-id="element-2446">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2447">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-2448" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2449">
              Quote Request Received!
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2450">
              Thank you, {formData.first_name}! A roofing specialist will
              contact you shortly at {formData.phone_primary} to discuss your{' '}
              {formData.project_type.toLowerCase()} project in {formData.city}.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors" data-id="element-2451">
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2452">
      <Header data-id="element-2453" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-2454" />

      <div className="w-full bg-slate-200 h-2" data-id="element-2455">
        <motion.div className="bg-amber-500 h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-2456" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-2457">
        <div className="w-full max-w-2xl" data-id="element-2458">
          <div className="mb-6 h-8" data-id="element-2459">
            {step > 1 && <button onClick={handleBack} className="text-slate-500 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors" data-id="element-2460">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2461" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-100" data-id="element-2462">
            <AnimatePresence mode="wait" custom={direction} data-id="element-2463">
              {/* STEP 1: Project Type */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2464">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2465">
                    What type of roofing project do you need?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2466">
                    {[{
                  value: 'Roof Replacement',
                  desc: 'Replace my entire roof or a large section',
                  icon: <RefreshCwIcon className="w-6 h-6" data-id="element-2467" />
                }, {
                  value: 'Roof Repair',
                  desc: 'Fix leaks, damaged shingles, or other issues',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-2468" />
                }, {
                  value: 'New Roof',
                  desc: 'Install a roof on new construction',
                  icon: <HomeIcon className="w-6 h-6" data-id="element-2469" />
                }, {
                  value: 'Inspection',
                  desc: 'Get a professional roof inspection',
                  icon: <SearchIcon className="w-6 h-6" data-id="element-2470" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('project_type', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.project_type === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2471">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.project_type === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2472">
                          {option.icon}
                        </div>
                        <div data-id="element-2473">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2474">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2475">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: Roofing Material */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2476">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2477">
                    What roofing material do you prefer?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2478">
                    {[{
                  value: 'Asphalt Shingles',
                  desc: 'Most popular and affordable option'
                }, {
                  value: 'Metal',
                  desc: 'Durable and energy-efficient'
                }, {
                  value: 'Tile',
                  desc: 'Classic look with long lifespan'
                }, {
                  value: 'Slate',
                  desc: 'Premium natural stone roofing'
                }, {
                  value: 'Flat / Low-Slope',
                  desc: 'For flat or nearly flat roofs'
                }, {
                  value: 'Not Sure',
                  desc: "I'd like a professional recommendation"
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('roofing_material', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.roofing_material === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2479">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.roofing_material === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2480">
                          <LayersIcon className="w-6 h-6" data-id="element-2481" />
                        </div>
                        <div data-id="element-2482">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2483">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2484">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Number of Stories */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2485">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2486">
                    How many stories is your home?
                  </h1>
                  <div className="grid grid-cols-3 gap-4" data-id="element-2487">
                    {['1 Story', '2 Stories', '3+ Stories'].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('stories', option)} className={`p-8 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData.stories === option ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2488">
                          <HomeIcon className={`w-8 h-8 ${formData.stories === option ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-2489" />
                          <span className="font-bold text-slate-900 text-lg" data-id="element-2490">
                            {option}
                          </span>
                        </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: Timeline */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2491">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2492">
                    When do you need this done?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2493">
                    {[{
                  value: 'Immediately',
                  desc: 'Emergency or urgent repair needed',
                  icon: <ZapIcon className="w-6 h-6" data-id="element-2494" />
                }, {
                  value: 'Within 1 Month',
                  desc: 'Ready to start soon',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2495" />
                }, {
                  value: '1-3 Months',
                  desc: 'Planning ahead',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2496" />
                }, {
                  value: '3-6 Months',
                  desc: 'Flexible timeline',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2497" />
                }, {
                  value: 'Just Researching',
                  desc: 'Gathering information and quotes',
                  icon: <SearchIcon className="w-6 h-6" data-id="element-2498" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('timeline', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.timeline === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2499">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.timeline === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2500">
                          {option.icon}
                        </div>
                        <div data-id="element-2501">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2502">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2503">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 5: Insurance Claim */}
              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2504">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2505">
                    Is this an insurance claim?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2506">
                    {[{
                  value: 'Yes',
                  desc: 'I have or plan to file an insurance claim',
                  icon: <ShieldIcon className="w-6 h-6" data-id="element-2507" />
                }, {
                  value: 'No',
                  desc: 'This is not an insurance-related project',
                  icon: <HomeIcon className="w-6 h-6" data-id="element-2508" />
                }, {
                  value: 'Not Sure',
                  desc: "I'm not sure if my damage is covered",
                  icon: <HelpCircleIcon className="w-6 h-6" data-id="element-2509" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('insurance_claim', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.insurance_claim === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2510">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.insurance_claim === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2511">
                          {option.icon}
                        </div>
                        <div data-id="element-2512">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2513">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2514">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 6: Property Ownership */}
              {step === 6 && <motion.div key="step6" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2515">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2516">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2517">
                    {[{
                  value: 'Own',
                  desc: 'I own this property',
                  icon: <HomeIcon className="w-6 h-6" data-id="element-2518" />
                }, {
                  value: 'Rent',
                  desc: 'I rent this property',
                  icon: <UserIcon className="w-6 h-6" data-id="element-2519" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('property_ownership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.property_ownership === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2520">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.property_ownership === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2521">
                          {option.icon}
                        </div>
                        <div data-id="element-2522">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2523">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2524">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 7: Address */}
              {step === 7 && <motion.div key="step7" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2525">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2526">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-2527">
                    <div data-id="element-2528">
                      <label htmlFor="address1" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2529">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} placeholder="123 Main St" data-id="element-2530" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-2531">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-2532">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-2533">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2534" />
                            <span data-id="element-2535">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-2536">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2537" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-2538" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full hover:bg-amber-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-2539">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2540" />
                            <span className="text-sm font-semibold text-amber-700" data-id="element-2541">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-amber-400 ml-auto" data-id="element-2542">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full hover:bg-slate-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-2543">
                            <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2544" />
                            <span className="text-sm text-slate-500" data-id="element-2545">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2546">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 8: Contact Info */}
              {step === 8 && <motion.div key="step8" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2547">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2548">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-slate-500 text-center mb-8" data-id="element-2549">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2550">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-2551">
                      <div data-id="element-2552">
                        <label htmlFor="first_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2553">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="First Name" data-id="element-2554" />
                      </div>
                      <div data-id="element-2555">
                        <label htmlFor="last_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2556">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Last Name" data-id="element-2557" />
                      </div>
                    </div>

                    <div data-id="element-2558">
                      <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2559">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="email@example.com" data-id="element-2560" />
                    </div>

                    <div data-id="element-2561">
                      <label htmlFor="phone_primary" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2562">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_primary" name="phone_primary" required value={formData.phone_primary} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-2563" />
                    </div>

                    <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-2564">
                      <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0" required data-id="element-2565" />
                      <span className="text-[11px] text-slate-500 leading-tight" data-id="element-2566">
                        By clicking the button, I provide my electronic
                        signature and express written consent to share my
                        contact information with FindServiceQuotes, including
                        their contractors and affiliates, and up to four home
                        service companies. I authorize them to email, call, or
                        text me for marketing purposes using autodialer
                        technology, AI generated or AI assisted voice, or
                        prerecorded voice messages on the phone number I
                        provided, even if listed on a DoNotCall list. Consent is
                        not a condition to purchase. I agree to the{' '}
                        <a href="#" className="underline text-amber-600" data-id="element-2567">
                          Terms of Use
                        </a>{' '}
                        and{' '}
                        <a href="#" className="underline text-amber-600" data-id="element-2568">
                          Privacy Policy
                        </a>
                        .
                      </span>
                    </label>
                    <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30" data-id="element-2569">
                      {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                    </button>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}
// ─── GENERIC QUOTE FORM (all other services) ───────────────────────────────
function GenericQuoteForm({
  initialService,
  initialZip,
  onClose
}: QuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const [formData, setFormData] = useState({
    serviceCategory: initialService || 'general',
    zipCode: initialZip || '',
    specificNeed: '',
    projectScope: '',
    timeline: '',
    homeownerStatus: '',
    address1: '',
    city: '',
    state: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.zipCode;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.zipCode]);
  // Load TrustedForm script
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
  }, []);
  const totalSteps = 6;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => {
      handleNext();
    }, 300);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') setAddressError('');
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  const getServiceDisplayName = (key: string) => {
    const names: Record<string, string> = {
      'walk-in-showers': 'Walk-In Shower',
      'walk-in-tubs': 'Walk-In Tub',
      stairlifts: 'Stairlift',
      'window-replacement': 'Window Replacement',
      'gutter-guards': 'Gutter Guard',
      roofing: 'Roofing',
      siding: 'Siding',
      solar: 'Solar Panel',
      'bathroom-remodel': 'Bathroom Remodel',
      'kitchen-remodel': 'Kitchen Remodel',
      flooring: 'Flooring',
      hvac: 'HVAC',
      painting: 'Painting',
      plumbing: 'Plumbing',
      electrical: 'Electrical',
      fencing: 'Fencing',
      insulation: 'Insulation',
      doors: 'Door',
      'garage-doors': 'Garage Door',
      'home-security': 'Home Security',
      'pest-control': 'Pest Control',
      landscaping: 'Landscaping',
      cleaning: 'Cleaning',
      general: 'General Repair'
    };
    return names[key] || key;
  };
  const serviceName = getServiceDisplayName(formData.serviceCategory);
  const getServiceOptions = () => {
    const category = formData.serviceCategory.toLowerCase();
    const optionsMap: Record<string, {
      label: string;
      icon: React.ReactNode;
    }[]> = {
      'walk-in-showers': [{
        label: 'New Installation',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2570" />
      }, {
        label: 'Replacement/Upgrade',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2571" />
      }, {
        label: 'Conversion from Tub',
        icon: <BathIcon className="w-6 h-6" data-id="element-2572" />
      }, {
        label: 'Accessibility Retrofit',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2573" />
      }],
      'walk-in-tubs': [{
        label: 'New Installation',
        icon: <BathIcon className="w-6 h-6" data-id="element-2574" />
      }, {
        label: 'Replacement/Upgrade',
        icon: <BathIcon className="w-6 h-6" data-id="element-2575" />
      }, {
        label: 'Conversion from Shower',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2576" />
      }, {
        label: 'Accessibility Features',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2577" />
      }],
      stairlifts: [{
        label: 'Straight Stairlift',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2578" />
      }, {
        label: 'Curved Stairlift',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2579" />
      }, {
        label: 'Outdoor Stairlift',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2580" />
      }, {
        label: 'Repair/Service',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2581" />
      }],
      'gutter-guards': [{
        label: 'New Gutter Guards',
        icon: <LayersIcon className="w-6 h-6" data-id="element-2582" />
      }, {
        label: 'Gutter Replacement',
        icon: <LayersIcon className="w-6 h-6" data-id="element-2583" />
      }, {
        label: 'Gutter Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2584" />
      }, {
        label: 'Cleaning & Maintenance',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2585" />
      }],
      roofing: [{
        label: 'Roof Repair',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2586" />
      }, {
        label: 'Full Replacement',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2587" />
      }, {
        label: 'Inspection',
        icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2588" />
      }, {
        label: 'Storm Damage',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2589" />
      }],
      siding: [{
        label: 'Full Replacement',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2590" />
      }, {
        label: 'Partial Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2591" />
      }, {
        label: 'New Installation',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2592" />
      }, {
        label: 'Inspection/Estimate',
        icon: <SearchIcon className="w-6 h-6" data-id="element-2593" />
      }],
      solar: [{
        label: 'Rooftop Solar Panels',
        icon: <SunIcon className="w-6 h-6" data-id="element-2594" />
      }, {
        label: 'Solar Battery Storage',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2595" />
      }, {
        label: 'Solar Roof Tiles',
        icon: <SunIcon className="w-6 h-6" data-id="element-2596" />
      }, {
        label: 'Consultation/Quote',
        icon: <SearchIcon className="w-6 h-6" data-id="element-2597" />
      }],
      'bathroom-remodel': [{
        label: 'Full Remodel',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2598" />
      }, {
        label: 'Shower/Tub Only',
        icon: <BathIcon className="w-6 h-6" data-id="element-2599" />
      }, {
        label: 'Vanity & Fixtures',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2600" />
      }, {
        label: 'Tile & Flooring',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2601" />
      }],
      'kitchen-remodel': [{
        label: 'Full Remodel',
        icon: <CookingPotIcon className="w-6 h-6" data-id="element-2602" />
      }, {
        label: 'Cabinets & Countertops',
        icon: <CookingPotIcon className="w-6 h-6" data-id="element-2603" />
      }, {
        label: 'Appliance Installation',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2604" />
      }, {
        label: 'Backsplash & Tile',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2605" />
      }],
      flooring: [{
        label: 'Hardwood',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2606" />
      }, {
        label: 'Tile',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2607" />
      }, {
        label: 'Carpet',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2608" />
      }, {
        label: 'Vinyl/Laminate',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2609" />
      }],
      plumbing: [{
        label: 'Repair/Fix Issue',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2610" />
      }, {
        label: 'New Installation',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2611" />
      }, {
        label: 'Drain Cleaning',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2612" />
      }, {
        label: 'Water Heater',
        icon: <ThermometerIcon className="w-6 h-6" data-id="element-2613" />
      }],
      electrical: [{
        label: 'Wiring/Rewiring',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2614" />
      }, {
        label: 'Panel Upgrade',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2615" />
      }, {
        label: 'Lighting Installation',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2616" />
      }, {
        label: 'Outlet/Switch Repair',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2617" />
      }],
      hvac: [{
        label: 'AC Repair',
        icon: <ThermometerIcon className="w-6 h-6" data-id="element-2618" />
      }, {
        label: 'Heating Repair',
        icon: <ThermometerIcon className="w-6 h-6" data-id="element-2619" />
      }, {
        label: 'New System Install',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2620" />
      }, {
        label: 'Routine Maintenance',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2621" />
      }],
      painting: [{
        label: 'Interior Painting',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2622" />
      }, {
        label: 'Exterior Painting',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2623" />
      }, {
        label: 'Cabinet Painting',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2624" />
      }, {
        label: 'Both Int/Ext',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2625" />
      }],
      fencing: [{
        label: 'New Fence Install',
        icon: <FenceIcon className="w-6 h-6" data-id="element-2626" />
      }, {
        label: 'Fence Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2627" />
      }, {
        label: 'Gate Installation',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2628" />
      }, {
        label: 'Fence Replacement',
        icon: <FenceIcon className="w-6 h-6" data-id="element-2629" />
      }],
      insulation: [{
        label: 'Attic Insulation',
        icon: <SnowflakeIcon className="w-6 h-6" data-id="element-2630" />
      }, {
        label: 'Wall Insulation',
        icon: <SnowflakeIcon className="w-6 h-6" data-id="element-2631" />
      }, {
        label: 'Spray Foam',
        icon: <SnowflakeIcon className="w-6 h-6" data-id="element-2632" />
      }, {
        label: 'Removal & Replace',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2633" />
      }],
      doors: [{
        label: 'Front Door',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2634" />
      }, {
        label: 'Patio/Sliding Door',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2635" />
      }, {
        label: 'Interior Doors',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2636" />
      }, {
        label: 'Door Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2637" />
      }],
      'garage-doors': [{
        label: 'New Installation',
        icon: <WarehouseIcon className="w-6 h-6" data-id="element-2638" />
      }, {
        label: 'Replacement',
        icon: <WarehouseIcon className="w-6 h-6" data-id="element-2639" />
      }, {
        label: 'Opener Install/Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2640" />
      }, {
        label: 'Repair/Maintenance',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2641" />
      }],
      'home-security': [{
        label: 'Full System Install',
        icon: <ShieldIcon className="w-6 h-6" data-id="element-2642" />
      }, {
        label: 'Camera System',
        icon: <ShieldIcon className="w-6 h-6" data-id="element-2643" />
      }, {
        label: 'Smart Lock Install',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2644" />
      }, {
        label: 'Alarm System',
        icon: <ShieldIcon className="w-6 h-6" data-id="element-2645" />
      }],
      'pest-control': [{
        label: 'General Pest Control',
        icon: <BugIcon className="w-6 h-6" data-id="element-2646" />
      }, {
        label: 'Termite Treatment',
        icon: <BugIcon className="w-6 h-6" data-id="element-2647" />
      }, {
        label: 'Rodent Control',
        icon: <BugIcon className="w-6 h-6" data-id="element-2648" />
      }, {
        label: 'Wildlife Removal',
        icon: <BugIcon className="w-6 h-6" data-id="element-2649" />
      }],
      cleaning: [{
        label: 'Regular Cleaning',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2650" />
      }, {
        label: 'Deep Cleaning',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2651" />
      }, {
        label: 'Move-in/Move-out',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2652" />
      }, {
        label: 'Post-Construction',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2653" />
      }],
      landscaping: [{
        label: 'Lawn Care/Mowing',
        icon: <TreesIcon className="w-6 h-6" data-id="element-2654" />
      }, {
        label: 'Landscape Design',
        icon: <TreesIcon className="w-6 h-6" data-id="element-2655" />
      }, {
        label: 'Tree Service',
        icon: <TreesIcon className="w-6 h-6" data-id="element-2656" />
      }, {
        label: 'Hardscaping',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2657" />
      }]
    };
    return optionsMap[category] || [{
      label: 'Repair/Maintenance',
      icon: <WrenchIcon className="w-6 h-6" data-id="element-2658" />
    }, {
      label: 'New Installation',
      icon: <HammerIcon className="w-6 h-6" data-id="element-2659" />
    }, {
      label: 'Replacement',
      icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2660" />
    }, {
      label: 'Other',
      icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2661" />
    }];
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2662">
        <header className="bg-white border-b border-slate-200 py-4 px-6 flex justify-between items-center" data-id="element-2663">
          <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2664">
            FindService<span className="text-amber-500" data-id="element-2665">Quotes</span>
          </div>
          <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-2666">
            Return to Home
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-2667">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100" data-id="element-2668">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2669">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-2670" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2671">
              Request Received!
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2672">
              Thanks {formData.firstName}! We're matching you with top-rated{' '}
              {serviceName} pros in {formData.zipCode}. They will contact you
              shortly.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors" data-id="element-2673">
              Done
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2674">
      <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-2675">
        <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2676">
          FindService<span className="text-amber-500" data-id="element-2677">Quotes</span>
        </div>
        <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-2678">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2679" />
          <span className="hidden sm:inline" data-id="element-2680">Exit</span>
        </button>
      </header>

      <div className="w-full bg-slate-200 h-2" data-id="element-2681">
        <motion.div className="bg-amber-500 h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-2682" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-2683">
        <div className="w-full max-w-2xl" data-id="element-2684">
          <div className="mb-6 h-8" data-id="element-2685">
            {step > 1 && <button onClick={handleBack} className="text-slate-500 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors" data-id="element-2686">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2687" /> Back
              </button>}
          </div>

          <div className="relative" data-id="element-2688">
            <AnimatePresence mode="wait" custom={direction} data-id="element-2689">
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2690">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2691">
                    What type of {serviceName} service do you need?
                  </h1>
                  <p className="text-slate-500 text-center mb-8 flex items-center justify-center gap-2" data-id="element-2692">
                    <MapPinIcon className="w-4 h-4" data-id="element-2693" /> Zip Code:{' '}
                    {formData.zipCode || 'Not provided'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2694">
                    {getServiceOptions().map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('specificNeed', option.label)} className={`p-6 rounded-xl border-2 text-left flex flex-col items-center justify-center gap-4 transition-all duration-200 ${formData.specificNeed === option.label ? 'border-amber-500 bg-amber-50 shadow-md ring-1 ring-amber-500' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2695">
                        <div className={`p-3 rounded-full ${formData.specificNeed === option.label ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-2696">
                          {option.icon}
                        </div>
                        <span className="font-semibold text-slate-900 text-lg" data-id="element-2697">
                          {option.label}
                        </span>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2698">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2699">
                    What is the scope of your {serviceName} project?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2700">
                    {[{
                  value: 'Complete Replacement',
                  desc: 'Entire system to be replaced',
                  icon: <ZapIcon className="w-6 h-6" data-id="element-2701" />
                }, {
                  value: 'Partial Replacement',
                  desc: 'Some components to be replaced',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-2702" />
                }, {
                  value: 'Repair/Upgrade',
                  desc: 'Fix existing issues and improve performance',
                  icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2703" />
                }, {
                  value: 'New Installation',
                  desc: 'Entire new system to be installed',
                  icon: <HammerIcon className="w-6 h-6" data-id="element-2704" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('projectScope', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.projectScope === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2705">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.projectScope === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2706">
                          {option.icon}
                        </div>
                        <div data-id="element-2707">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2708">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2709">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2710">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2711">
                    How long do you need this completed?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2712">
                    {[{
                  value: 'Immediate',
                  desc: 'Need it done as soon as possible',
                  icon: <ZapIcon className="w-6 h-6" data-id="element-2713" />
                }, {
                  value: 'Within 1 Week',
                  desc: 'Ready to start in the next week',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2714" />
                }, {
                  value: '1-3 Weeks',
                  desc: 'Planning ahead for a few weeks',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2715" />
                }, {
                  value: '3-6 Weeks',
                  desc: 'Flexible timeline for several weeks',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2716" />
                }, {
                  value: 'Just Researching',
                  desc: 'Gathering information and quotes',
                  icon: <SearchIcon className="w-6 h-6" data-id="element-2717" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('timeline', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.timeline === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2718">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.timeline === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2719">
                          {option.icon}
                        </div>
                        <div data-id="element-2720">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2721">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2722">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2723">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2724">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2725">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No - But I am Allowed to make changes',
                  desc: 'I rent but have permission from my landlord'
                }, {
                  value: 'No',
                  desc: 'I rent and do not have permission'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeownerStatus', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeownerStatus === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2726">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeownerStatus === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2727">
                          <UserIcon className="w-6 h-6" data-id="element-2728" />
                        </div>
                        <div data-id="element-2729">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2730">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2731">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2732">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2733">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-2734">
                    <div data-id="element-2735">
                      <label htmlFor="address1" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2736">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} placeholder="123 Main St" data-id="element-2737" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-2738">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-2739">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-2740">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2741" />
                            <span data-id="element-2742">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-2743">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2744" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-2745" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full hover:bg-amber-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-2746">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2747" />
                            <span className="text-sm font-semibold text-amber-700" data-id="element-2748">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-amber-400 ml-auto" data-id="element-2749">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full hover:bg-slate-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-2750">
                            <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2751" />
                            <span className="text-sm text-slate-500" data-id="element-2752">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2753">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {step === 6 && <motion.div key="step6" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2754">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2755">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-slate-500 text-center mb-8" data-id="element-2756">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2757">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-2758">
                      <div data-id="element-2759">
                        <label htmlFor="firstName" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2760">
                          First Name
                        </label>
                        <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="First Name" data-id="element-2761" />
                      </div>
                      <div data-id="element-2762">
                        <label htmlFor="lastName" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2763">
                          Last Name
                        </label>
                        <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Last Name" data-id="element-2764" />
                      </div>
                    </div>

                    <div data-id="element-2765">
                      <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2766">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="email@example.com" data-id="element-2767" />
                    </div>

                    <div data-id="element-2768">
                      <label htmlFor="phone" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2769">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-2770" />
                    </div>

                    <div className="pt-4" data-id="element-2771">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-2772">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0" required data-id="element-2773" />
                        <span className="text-[11px] text-slate-500 leading-tight" data-id="element-2774">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with FindServiceQuotes, including
                          their contractors and affiliates, and up to four home
                          service companies. I authorize them to email, call, or
                          text me for marketing purposes using autodialer
                          technology, AI generated or AI assisted voice, or
                          prerecorded voice messages on the phone number I
                          provided, even if listed on a DoNotCall list. Consent
                          is not a condition to purchase. I agree to the{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2775">
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2776">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30" data-id="element-2777">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}